import { useState } from 'react'
import { queueMilestoneMutation } from '../../utils/rockSaveQueue'
import { RockMilestone, User } from '../../types'
import MilestoneItem from './MilestoneItem'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  milestones: RockMilestone[]
  teamId: number
  rockId: number
  teamMembers: User[]
  /** Milestones need a saved rock to hang off — see the Rock Planner's create mode. */
  disabled?: boolean
  disabledHint?: string
  /** Fires after a milestone mutation lands, for callers that hold their own copy of the rock. */
  onChanged?: () => void
  onBeforeChange?: () => void
}

export default function MilestoneList({ milestones, teamId, rockId, teamMembers, disabled, disabledHint, onChanged, onBeforeChange }: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [createActionItem, setCreateActionItem] = useState(false)
  const [newAssigneeId, setNewAssigneeId] = useState('')
  const [newDueDate, setNewDueDate] = useState('')

  const resetForm = () => {
    setNewTitle('')
    setAdding(false)
    setCreateActionItem(false)
    setNewAssigneeId('')
    setNewDueDate('')
  }

  const handleAdd = () => {
    if (!newTitle.trim() || saving) return
    onBeforeChange?.()
    setSaving(true)
    setError(null)
    queueMilestoneMutation('post', `/teams/${teamId}/rocks/${rockId}/milestones`, {
      milestone: {
        title: newTitle.trim(),
        assignee_id: newAssigneeId || null,
        due_date: newDueDate || null
      },
      create_action_item: createActionItem
    }, {
      onSuccess: () => { setSaving(false); resetForm(); onChanged?.() },
      onError: (_errors, message) => { setSaving(false); setError(message) },
    })
  }

  const sorted = [...milestones].sort((a, b) => a.position - b.position)

  if (disabled) {
    return (
      <div aria-disabled="true">
        <h4 className="text-sm font-semibold text-text-muted">Milestones</h4>
        {disabledHint && (
          <p className="mt-1 text-xs text-text-muted">{disabledHint}</p>
        )}
        <button
          type="button"
          disabled
          className="mt-2 px-3 py-1.5 text-sm text-text-muted font-medium cursor-not-allowed opacity-60"
        >
          + Add Milestone
        </button>
      </div>
    )
  }

  return (
    <div>
      {error && <p role="alert" className="text-xs text-pinnacle-red">{error}</p>}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-text-primary">
          Milestones
          {milestones.length > 0 && (
            <span className="font-normal text-text-muted ml-1">
              ({milestones.filter(m => m.status === 'complete').length}/{milestones.length})
            </span>
          )}
        </h4>
      </div>

      <div className="space-y-0.5">
        {sorted.map(m => (
          <MilestoneItem
            key={m.id}
            milestone={m}
            teamId={teamId}
            rockId={rockId}
            teamMembers={teamMembers}
            onChanged={onChanged}
            onBeforeChange={onBeforeChange}
          />
        ))}
      </div>

      {adding ? (
        <div className="mt-2 px-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="Milestone title"
              className="flex-1 px-2 py-1.5 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') resetForm() }}
            />
            <button onClick={handleAdd} className="text-sm text-pinnacle-red font-medium">Add</button>
            <button onClick={resetForm} className="text-sm text-text-muted">Cancel</button>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={newAssigneeId}
              onChange={e => setNewAssigneeId(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 bg-white"
              aria-label="Assignee"
            >
              <option value="">Unassigned</option>
              {sortPeople(teamMembers).map(member => (
                <option key={member.id} value={member.id}>{member.full_name}</option>
              ))}
            </select>
            <input
              type="date"
              value={newDueDate}
              onChange={e => setNewDueDate(e.target.value)}
              className="px-2 py-1.5 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
              aria-label="Due date"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={createActionItem}
              onChange={e => setCreateActionItem(e.target.checked)}
              className="w-4 h-4 rounded border-stone/40 text-pinnacle-red focus:ring-pinnacle-red/20"
            />
            Also create as action item
          </label>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-2 px-3 py-1.5 text-sm text-pinnacle-red hover:text-pinnacle-red/80 font-medium"
        >
          + Add Milestone
        </button>
      )}
    </div>
  )
}
